import { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { runIdentityCodeBootstrap } from '@/services/identity-code-bootstrap';

// Lazy-load the game page (Phaser is ~700KB, only load when needed)
const OtterCampPage = lazy(() => import('@/pages/OtterCampPage'));

function PageLoader() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900">
      <div className="w-10 h-10 border-4 border-gray-600 border-t-green-500 rounded-full animate-spin mb-4" />
      <p className="text-white">Loading Otter Camp...</p>
    </div>
  );
}

function App() {
  // Unified-identity rehydrate (ADR-015 / ADR-024). The identity-service session
  // bootstrap settles BEFORE the auth gate evaluates, because the JWT store is
  // in-memory (empty on every fresh load) and Otter Camp's auth gate (the
  // AuthCTA overlay, gated on checkSession()) decides once on mount whether the
  // member is logged in. Running the rehydrate first means a DAO member who
  // navigated here from the portal — carrying the shared httpOnly hwdao_refresh
  // cookie — is already authenticated when the gate runs, instead of being
  // bounced to login.
  //
  // It runs on EVERY load, handling two cases (see services/identity-code-bootstrap):
  //   - `?code=` login return → exchange the one-time code for a JWT.
  //   - plain load / cross-suite navigation (no `?code=`) → silent refresh from
  //     the shared hwdao_refresh cookie so the session rehydrates.
  //
  // While the bootstrap is in flight we hold the app behind a loader.
  const [bootstrapPending, setBootstrapPending] = useState(true);

  useEffect(() => {
    let mounted = true;
    void runIdentityCodeBootstrap().then(() => {
      if (mounted) setBootstrapPending(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (bootstrapPending) {
    return <PageLoader />;
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/otter-camp" element={<OtterCampPage />} />
          <Route path="/" element={<Navigate to="/otter-camp" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
